/**
 * Media Filter Bar
 *
 * Contract:
 * - May be bundled globally
 * - Must silently no-op if required DOM isn't present
 * - Must be idempotent (no duplicate listeners)
 */

import { trackMediaPlay } from '../../utils/trackMediaPlay.js';

function initMediaFilterBar() {
    const mediaTypeFilter = document.getElementById('media-type-filter');
    const statusFilter = document.getElementById('status-filter');
    const categoryFilter = document.getElementById('category-filter');

    // Exit early if we're not on the media page
    if (!mediaTypeFilter || !statusFilter) return;

    // Idempotency guard (prevents double-binding)
    // Use one of the core elements as the guard target.
    if (mediaTypeFilter.dataset.mediaFilterInitialized === 'true') return;
    mediaTypeFilter.dataset.mediaFilterInitialized = 'true';

    const resultsCount = document.getElementById('results-count');
    const noResultsMessage = document.getElementById('no-results-message');

    function updateResultsCount(visible, total) {
        if (!resultsCount) return;

        if (visible === 0) {
            resultsCount.textContent = 'No items found';
        } else if (visible === total) {
            resultsCount.textContent = `Showing all ${total} items`;
        } else {
            resultsCount.textContent = `Showing ${visible} of ${total} items`;
        }
    }

    function updateFilters() {
        const selectedMediaType = mediaTypeFilter.value;
        const selectedStatus = statusFilter.value;
        const selectedCategory = categoryFilter ? categoryFilter.value : 'all';

        // Re-query items each run to support dynamic inserts/removals
        const mediaItems = document.querySelectorAll('.media-item');

        let visibleCount = 0;

        mediaItems.forEach((item) => {
            const itemType = item.getAttribute('data-type');
            const itemStatus = item.getAttribute('data-status');
            const itemCategory = item.getAttribute('data-category');

            let showItem = true;

            // Filter by media type
            if (selectedMediaType !== 'all' && itemType !== selectedMediaType) {
                showItem = false;
            }

            // Filter by status
            if (selectedStatus !== 'all' && itemStatus !== selectedStatus) {
                showItem = false;
            }

            // Filter by category
            if (selectedCategory !== 'all' && itemCategory !== selectedCategory) {
                showItem = false;
            }

            // Show/hide item
            if (showItem) {
                item.classList.remove('filtered-out');
                visibleCount++;
            } else {
                item.classList.add('filtered-out');
            }
        });

        // Show/hide no results message
        if (noResultsMessage) {
            noResultsMessage.style.display = visibleCount === 0 ? 'block' : 'none';
        }

        // Update results count
        updateResultsCount(visibleCount, mediaItems.length);
    }

    // Add event listeners (idempotent guard prevents duplicates)
    mediaTypeFilter.addEventListener('change', updateFilters);
    statusFilter.addEventListener('change', updateFilters);
    if (categoryFilter) categoryFilter.addEventListener('change', updateFilters);

    // Apply once on load so initial state always matches select values
    updateFilters();
}

// ─── YouTube Facade ───────────────────────────────────────────────────────────
// Delays iframe creation until the user clicks play, preventing YouTube from
// setting cookies on page load (privacy-enhanced mode via youtube-nocookie.com).

function initYoutubeFacades() {
    document.querySelectorAll('.youtube-facade').forEach((facade) => {
        if (facade.dataset.facadeInitialized) return;
        facade.dataset.facadeInitialized = 'true';

        facade.addEventListener('click', () => {
            const embedUrl = facade.dataset.embedUrl;
            const title = facade.dataset.title || '';
            const iframe = document.createElement('iframe');
            iframe.src = embedUrl;
            iframe.title = title;
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute('allowfullscreen', '');
            iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
            facade.replaceWith(iframe);
            trackMediaPlay(facade.closest('[data-media-item]')?.dataset.mediaItem);
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMediaFilterBar);
    document.addEventListener('DOMContentLoaded', initYoutubeFacades);
} else {
    initMediaFilterBar();
    initYoutubeFacades();
}
